import { API } from "./api";
import { AppNav } from "./app-nav";
import { AppConstants } from "./constants";
import {
  getDefaultAlgorithmFromMap,
  getDefaultCoinByAlgorithmFromMap,
} from "./shared/coin-utils";

export enum TimeInterval {
  Min5 = 300,
  Min15 = 900,
  Min30 = 1_800,
  Hour1 = 3_600,
  Hour4 = 14_400,
  Day1 = 86_400,
}

export function provideSession(): string | null {
  if (typeof window == "undefined") return null;
  return localStorage.getItem(AppConstants.SessionId);
}

export function provideSessionFromLocal(): string | null {
  if (typeof window == "undefined") return null;
  return localStorage.getItem(AppConstants.SessionId);
}

export function clearSession() {
  if (typeof window == "undefined") return;

  localStorage.removeItem(AppConstants.SessionId);
}

export function checkSessionError(status: string): boolean {
  return status == API.Status.ActionUnknownId;
}

export function mapFromRecord<T>(data: Record<string, T>): Map<string, T> {
  const result = new Map<string, T>();

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      result.set(key, data[key]);
    }
  }

  return result;
}

export async function copyToClipboardWithFallback(
  text: string
): Promise<boolean> {
  if (typeof navigator != "undefined" && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // continue to legacy fallback
    }
  }

  if (typeof document == "undefined") return false;

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.opacity = "0";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    document.body.removeChild(textArea);
  }

  return copied;
}

export interface IResolvedCoinSelection {
  algorithm: string;
  coin: string;
}

export function getDefaultCoinByAlgorithm(
  coinsMap: Map<string, string[]>,
  algorithm: string
): string {
  const resolvedCoin = getDefaultCoinByAlgorithmFromMap(coinsMap, algorithm);
  return resolvedCoin ?? algorithm;
}

export function resolveCoinSelection(
  coinsMap: Map<string, string[]> | undefined,
  rememberedAlgorithm: string | undefined,
  rememberedCoin: string | undefined
): IResolvedCoinSelection | undefined {
  if (!coinsMap || coinsMap.size == 0) return undefined;

  let algorithm = rememberedAlgorithm;
  if (!algorithm || !coinsMap.has(algorithm)) {
    algorithm = findAlgorithmByCoin(coinsMap, rememberedCoin);
  }

  if (!algorithm || !coinsMap.has(algorithm)) {
    algorithm = getDefaultAlgorithmFromMap(coinsMap);
  }
  if (!algorithm) return undefined;

  const coins = coinsMap.get(algorithm) ?? [];
  let coin = rememberedCoin;
  if (!coin || coins.indexOf(coin) == -1) {
    coin = getDefaultCoinByAlgorithm(coinsMap, algorithm);
  }

  return { algorithm, coin };
}

function findAlgorithmByCoin(
  coinsMap: Map<string, string[]>,
  coin: string | undefined
): string | undefined {
  if (!coin) return undefined;

  for (const [algorithm, coins] of coinsMap.entries()) {
    if (coins.indexOf(coin) != -1) {
      return algorithm;
    }
  }

  return undefined;
}

interface ISearchParamsLike {
  get: (name: string) => string | null;
  toString: () => string;
}

function normalizeCoinValue(coin: string | null | undefined): string | undefined {
  if (!coin) return undefined;
  const normalized = coin.trim();
  if (normalized.length == 0) return undefined;
  return normalized;
}

export function getCoinFromSearchParams(
  searchParams: ISearchParamsLike | undefined | null
): string | undefined {
  if (!searchParams) return undefined;
  return normalizeCoinValue(searchParams.get("coin"));
}

export function buildPathnameWithSearch(
  pathname: string,
  searchParams: ISearchParamsLike | undefined | null
): string {
  const query = searchParams?.toString();
  if (!query) return pathname;
  return `${pathname}?${query}`;
}

export function buildPathWithCoin(
  pathname: string,
  searchParams: ISearchParamsLike | undefined | null,
  coin: string | undefined
): string {
  const params = new URLSearchParams(searchParams?.toString() ?? "");
  params.delete("alg");

  const normalizedCoin = normalizeCoinValue(coin);
  if (normalizedCoin) params.set("coin", normalizedCoin);
  else params.delete("coin");

  const query = params.toString();
  if (!query) return pathname;
  return `${pathname}?${query}`;
}

export function buildAuthPathWithNext(
  pathname: string,
  searchParams: ISearchParamsLike | undefined | null,
  coin: string | undefined
): string {
  const params = new URLSearchParams(searchParams?.toString() ?? "");

  params.delete("next");
  params.delete("alg");
  params.set("auth", "1");

  const normalizedCoin = normalizeCoinValue(coin);
  if (normalizedCoin) params.set("coin", normalizedCoin);
  else params.delete("coin");

  const query = params.toString();
  if (!query) return AppNav.Root;
  return `${AppNav.Root}?${query}`;
}

export function sanitizeNextPath(
  nextPath: string | null | undefined
): string | undefined {
  if (!nextPath) return undefined;
  if (!nextPath.startsWith("/")) return undefined;
  if (nextPath.startsWith("//")) return undefined;
  if (nextPath == `/${AppNav.Auth}`) return undefined;
  if (nextPath.startsWith(`/${AppNav.Auth}?`)) return undefined;
  return nextPath;
}
